import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styles from './styles.module.scss'
import branchApi from "../../store/branchStore/branchApi";
import { Roles } from "../../config";
import Checkbox from "../../components/FormElements/Checkbox";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useCurrentBranch } from "../../hooks/useCurrentBranch";
import { store } from "react-notifications-component";
import { useHistory } from "react-router-dom";
import { format } from 'date-fns';
import Select from "react-dropdown-select";
import UploadImage from "./UploadImage";
import TextField from "../../components/FormElements/TextField";
import VideoModal from "../../components/VideoModal";
import YOUTUBE_LINKS from "../../config/youtubeLinks";
import { deriveChannelsView, isChannelConnected } from "../WhatsApp/channelHelpers";
import FailedNotificationLine from "../../components/FailedNotificationLine";

const MAX_BONUS_EXPIRE_DAYS = 3650

const numberInputStyle = {
  padding: '8px 12px',
  border: '1px solid #E7E7E6',
  borderRadius: '5px',
  fontSize: '14px',
  width: '120px'
}

const ClientsDatabaseSendNotification = () => {
  const { info } = useSelector(s => s.branchReducer);
  const brandedWabaConnected = isChannelConnected(deriveChannelsView(info).channel_1);
  const history = useHistory()
  const { t, i18n } = useTranslation();
  const [modalPending, setModalPending] = useState(false);
  const [pushMessage, setPushMessage] = useState('')
  const [bonusAmount, setBonusAmount] = useState('')
  const [bonusExpireDays, setBonusExpireDays] = useState('')
  const [disableAutonotificationStatus, setDisableAutonotificationStatus] = useState(false);
  const [historyOfNotifications, setHistoryOfNotifications] = useState([])
  const { currentRole, currentBranch, groupId } = useCurrentBranch();
  const currentBranchId = currentBranch?.id ?? info?._id ?? info?.id ?? null;
  const [activeHistoryBranchId, setActiveHistoryBranchId] = useState(null)
  const [branchIds, setBranchIds] = useState([]);
  const [senderBranchId, setSenderBranchId] = useState(currentBranchId);
  const [image, setImage] = useState(null)
  const [buttonLinks, setButtonLinks] = useState([
    { link: '', label: '' }
  ])
  const hasInitializedMailingSelection = useRef(false)
  const senderBranchTouched = useRef(false)
  const [type, setType] = useState({
    label: t('text'),
    value: 'text',
  })

  const onChangePushMessage = (e) => {
    if (e.target.value.length <= 1200) {
      setPushMessage(e.target.value);
    }
  }

  const onChangeBonusAmount = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && !isNaN(parseInt(value)))) {
      setBonusAmount(value);
    }
  }

  const onChangeBonusExpireDays = (e) => {
    const value = e.target.value;
    if (value === '' || parseInt(value) >= 1) {
      setBonusExpireDays(value);
    }
  }

  const onDisableAutoNotification = () => {
    if (!info) return

    setDisableAutonotificationStatus(true);
    branchApi.branchesCustomersUploadedStop(info.group_id, {
      branch_ids: branchIds.length ? branchIds : historyOfNotifications.map(({ branch_id }) => branch_id)
    })
      .then(() => {
        store.addNotification({
          title: t('success'),
          message: 'Рассылка успешно остановлена',
          type: "success",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });
      })
      .finally(() => {
        setDisableAutonotificationStatus(false);
      });
  }

  const onSendPushToCustomers = (e) => {
    e.preventDefault();

    if (!info || !pushMessage) return

    const bonus = parseInt(bonusAmount)
    const expireDays = parseInt(bonusExpireDays)
    if (bonus > 0 && !(expireDays >= 1 && expireDays <= MAX_BONUS_EXPIRE_DAYS)) return

    setModalPending(true);
    if (currentRole === Roles.ADMINISTRATOR && groupId) {
      const formData = new FormData()
      formData.append('message', pushMessage)
      branchIds.forEach(id => formData.append('branch_ids', id))
      formData.append('type', type.value)
      if (senderBranchId) {
        formData.append('sender_branch_id', senderBranchId)
      }
      if (type.value === 'file') {
        formData.append('image', image)
      }
      if (type.value === 'button-links') {
        buttonLinks.forEach(({ label }) => formData.append('button_labels', label))
        buttonLinks.forEach(({ link }) => formData.append('button_urls', link))
      }

      const promises = [
        branchApi.branchesAnalyticCustomersUploadedSend(groupId, formData)
      ];

      if (bonus > 0 && currentBranch?.id) {
        promises.push(
          branchApi.branchesCustomersUploadedMassSendBonuses(currentBranch.id, {
            amount: bonus,
            expire_days: expireDays
          })
        );
      }

      Promise.all(promises)
        .then((results) => {
          let successMessage = t('successMessageSent');

          if (results.length > 1) {
            const bonusResults = results.slice(1);
            const totalUsersReceivingBonuses = bonusResults.reduce((sum, result) =>
              sum + (result.data?.users_count || 0), 0
            );
            successMessage += ` Бонусы отправлены ${totalUsersReceivingBonuses} пользователям.`;
          }

          store.addNotification({
            title: t('success'),
            message: successMessage,
            type: "success",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });
          setPushMessage('');
          setBonusAmount('');
          setBonusExpireDays('');
          setButtonLinks([{ label: '', link: '' }]);
          setImage(null);
        })
        .catch(() => {
          store.addNotification({
            title: t('error'),
            message: 'Ошибка при отправке уведомления или бонусов',
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
              duration: 5000,
              onScreen: true
            }
          });
        })
        .finally(() => {
          setModalPending(false);
        });
    }
  }

  useEffect(() => {
    let intervalId = null
    if (info?.group_id) {
      hasInitializedMailingSelection.current = false;

      branchApi.branchesCustomersUploadedHistory(info.group_id)
        .then((response) => {
          const data = response?.data || [];
          const ids = data.map(item => item.branch_id);

          setHistoryOfNotifications(data)
          setBranchIds((prev) => {
            if (!hasInitializedMailingSelection.current) {
              hasInitializedMailingSelection.current = true;
              return ids;
            }

            return prev.filter((id) => ids.includes(id));
          });
          setActiveHistoryBranchId((prev) => ids.includes(prev) ? prev : ids[0] || null);
        })

      intervalId = setInterval(() => {
        branchApi.branchesCustomersUploadedHistory(info.group_id)
          .then((response) => {
            const data = response?.data || [];
            const ids = data.map(item => item.branch_id);

            setHistoryOfNotifications(data)
            setBranchIds((prev) => prev.filter((id) => ids.includes(id)));
            setActiveHistoryBranchId((prev) => ids.includes(prev) ? prev : ids[0] || null);
          })
      }, 10000)
    }

    return () => {
      intervalId && clearInterval(intervalId)
    }
  }, [info?.group_id]);

  useEffect(() => {
    setImage(null)
    setButtonLinks([
      { link: '', label: '' }
    ])
  }, [type])

  const showStatus = (item) => {
    if (!item.history || !item.history.length) return styles.passive
    return item.history[item.history.length - 1].status === 'IN_PROGRESS' ? styles.active : styles.passive
  }

  const activeHistoryBranch = useMemo(() => {
    if (!historyOfNotifications.length) return null;

    return historyOfNotifications.find((item) => item.branch_id === activeHistoryBranchId) || historyOfNotifications[0];
  }, [activeHistoryBranchId, historyOfNotifications]);

  const allBranchIds = useMemo(() => {
    return historyOfNotifications.map(({ branch_id }) => branch_id);
  }, [historyOfNotifications]);

  const isAllBranchesSelected = allBranchIds.length > 0 && branchIds.length === allBranchIds.length;

  const getLatestHistoryItem = useCallback((branchHistory = []) => {
    if (!branchHistory || !branchHistory.length) return null;

    return branchHistory[branchHistory.length - 1];
  }, []);

  const toggleBranchForMailing = useCallback((branchId) => {
    setBranchIds((prev) => {
      if (prev.includes(branchId)) {
        return prev.filter((id) => id !== branchId);
      }

      return [...prev, branchId];
    });
  }, []);

  useEffect(() => {
    if (!senderBranchTouched.current && currentBranchId) {
      setSenderBranchId(currentBranchId);
    }
  }, [currentBranchId]);

  const senderOptions = useMemo(() => {
    const branches = historyOfNotifications.map(({ branch_id, name }) => ({ label: name, value: branch_id }));

    return [...branches, { label: 'С номера каждого филиала', value: null }];
  }, [historyOfNotifications]);

  const types = useMemo(() => {
    return [
      {
        label: t('text'),
        value: 'text',
      },
      {
        label: t('file'),
        value: 'file',
      },
      {
        label: t('button-links'),
        value: 'button-links',
      },
    ]
  }, [i18n.language, t])

  return (
    <div className={styles.wrapper}>
      {brandedWabaConnected && (
        <div
          style={{
            backgroundColor: '#fff8e1',
            border: '1px solid #ffe2a8',
            color: '#8a6d3b',
            padding: '12px 16px',
            borderRadius: 10,
            marginBottom: 16,
          }}
        >
          {t('NSCampaigns.legacyWabaWarning')}{' '}
          <a href="/campaigns" style={{ color: '#8a6d3b', fontWeight: 600 }}>
            {t('NSSidebar.campaigns')}
          </a>
        </div>
      )}
      <div className={styles.container}>
        <div className={styles.leftContent}>
          <div className={styles.modalContainer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <i
                className="fa fa-chevron-left"
                aria-hidden="true"
                style={{ cursor: 'pointer' }}
                onClick={() => history.push('/clients-database')}
              />
              <VideoModal url={YOUTUBE_LINKS.Mailing} />
            </div>
            <div className={styles.header}>
              <p>{t('NSClients.not')}</p>
            </div>
            <Select
              placeholder={t('chooseType')}
              values={[type]}
              options={types}
              onChange={(values) => setType(values[0])}
              style={{ height: 42, border: '1px solid #E7E7E6', borderRadius: '5px', marginBottom: '1rem' }}
            />
            <p className={styles.p}>Отправлять с номера</p>
            <Select
              placeholder="С номера каждого филиала"
              values={senderOptions.filter(({ value }) => value === senderBranchId)}
              options={senderOptions}
              onChange={(values) => {
                senderBranchTouched.current = true;
                setSenderBranchId(values[0]?.value ?? null);
              }}
              style={{ height: 42, border: '1px solid #E7E7E6', borderRadius: '5px', marginBottom: '1rem' }}
            />
            <form onSubmit={onSendPushToCustomers}>
              <p className={styles.p}>{t('NSClients.textNot')}</p>
              <textarea
                placeholder={t('Form.enterText')}
                className={styles.textArea}
                value={pushMessage}
                onChange={onChangePushMessage}
                required
              />
              <div className={styles.span}
                style={{ color: `${pushMessage.length >= 624 ? 'tomato' : '#000000'}` }}>
                {t('maxSym', { sym: 1200 })}
              </div>

              <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ minWidth: 'fit-content', fontWeight: '500' }}>
                  Количество подарочных бонусов:
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={bonusAmount}
                  onChange={onChangeBonusAmount}
                  placeholder="0"
                  style={numberInputStyle}
                />
              </div>

              {parseInt(bonusAmount) > 0 && (
                <>
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="bonus-expire-days" style={{ minWidth: 'fit-content', fontWeight: '500' }}>
                      Срок действия бонусов, дней:
                    </label>
                    <input
                      id="bonus-expire-days"
                      type="number"
                      min="1"
                      max={MAX_BONUS_EXPIRE_DAYS}
                      step="1"
                      required
                      value={bonusExpireDays}
                      onChange={onChangeBonusExpireDays}
                      style={numberInputStyle}
                    />
                  </div>
                  {!info?.configuration?.private_cashback?.is_cascade && (
                    <p style={{ marginTop: 8, fontSize: 12, color: '#8a6d3b' }}>
                      Накопительный режим: если у клиента уже есть бонусы за рассылку с более поздним сроком или бессрочные, подарок получит их срок, а не указанный.
                    </p>
                  )}
                </>
              )}

              {type.value === 'file' && (
                <div style={{ width: '80%', marginTop: '20px' }}>
                  <UploadImage
                    onChange={(nextImage) => setImage(nextImage)}
                    image={image}
                  />
                </div>
              )}

              {type.value === 'button-links' && (
                <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {buttonLinks.length < 3 && (
                    <ButtonFilled
                      onClick={() => setButtonLinks([...buttonLinks, { link: '', label: '' }])}
                    >
                      {t('addLink')}
                    </ButtonFilled>
                  )}
                  {buttonLinks.map((item, index) => (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', border: '1px solid #E7E7E6', gap: 12, borderRadius: '5px', padding: '16px' }}>
                      <div>
                        <TextField
                          label={t('title')}
                          required
                          onChange={(e) => {
                            if (e.target.value.length <= 25) {
                              setButtonLinks(
                                buttonLinks.map((buttonItem, idx) => idx === index ? { ...buttonItem, label: e.target.value } : buttonItem)
                              )
                            }
                          }}
                        />
                      </div>
                      <div>
                        <TextField
                          label={t('link')}
                          type="url"
                          required
                          onChange={(e) => {
                            setButtonLinks(
                              buttonLinks.map((buttonItem, idx) => idx === index ? { ...buttonItem, link: e.target.value } : buttonItem)
                            )
                          }}
                        />
                      </div>
                      {index ? (
                        <ButtonFilled
                          variant="red"
                          onClick={() => setButtonLinks(buttonLinks.filter((buttonItem, idx) => idx !== index))}
                        >
                          {t('delete')}
                        </ButtonFilled>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexDirection: 'column', marginTop: '16px' }}>
                <ButtonFilled
                  className={styles.stopBtn}
                  onClick={onDisableAutoNotification}
                  type="button"
                  disabled={!branchIds.length}
                  loading={modalPending || disableAutonotificationStatus}
                >
                  Прервать предыдущую авторассылку
                </ButtonFilled>
                <ButtonFilled
                  type="submit"
                  className={styles.btn}
                  disabled={!branchIds.length || !pushMessage}
                  loading={modalPending || disableAutonotificationStatus}
                >
                  {t('send')}
                </ButtonFilled>
                <ButtonFilled variant="purple" className={styles.stopBtn} onClick={() => history.push('/clients-database/send-notifications/exclude-number')}>
                  {t('NSSecurity.excludeNumber')}
                </ButtonFilled>
              </div>
            </form>
          </div>
        </div>

        <div className={styles.rightContent}>
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <p className={styles.p}>Филиалы для рассылки</p>
              <span className={styles.panelHint}>Чекбоксы влияют на отправку и остановку рассылки</span>
            </div>

            {historyOfNotifications.length ? (
              <ul className={styles.branchList}>
                <li className={styles.branchListHeader}>
                  <div>
                    <Checkbox
                      checked={isAllBranchesSelected}
                      aria-label="Выбрать все филиалы для рассылки"
                      onChange={() => {
                        if (isAllBranchesSelected) {
                          setBranchIds([])
                        } else {
                          setBranchIds(allBranchIds)
                        }
                      }}
                    />
                  </div>
                  <div>Филиал</div>
                  <div>Статус</div>
                </li>
                {historyOfNotifications.map((branchHistory) => {
                  const latestHistoryItem = getLatestHistoryItem(branchHistory.history);

                  return (
                    <li
                      key={branchHistory.branch_id}
                      className={`${styles.branchListItem} ${showStatus(branchHistory)}`}
                    >
                      <div>
                        <Checkbox
                          checked={branchIds.includes(branchHistory.branch_id)}
                          aria-label={`Выбрать ${branchHistory.name} для рассылки`}
                          onChange={() => toggleBranchForMailing(branchHistory.branch_id)}
                        />
                      </div>
                      <div className={styles.branchMainInfo}>
                        <span className={styles.branchName}>{branchHistory.name}</span>
                        <span className={styles.branchMeta}>
                          Историй: {branchHistory.history?.length || 0}
                          {latestHistoryItem?.sent_count ? ` · Последняя отправка: ${latestHistoryItem.sent_count}` : ''}
                        </span>
                      </div>
                      <div className={styles.branchStatusText}>
                        {latestHistoryItem?.status === 'IN_PROGRESS' ? 'Идёт' : 'Завершена'}
                      </div>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className={styles.emptyState}>Нет филиалов для рассылки</div>
            )}
          </div>

          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <p className={styles.p}>Просмотр истории</p>
              <span className={styles.panelHint}>Выбор ниже влияет только на отображение истории</span>
            </div>

            {historyOfNotifications.length ? (
              <div className={styles.historyBranchSelector}>
                {historyOfNotifications.map((branchHistory) => (
                  <button
                    key={branchHistory.branch_id}
                    type="button"
                    aria-label={`Открыть историю ${branchHistory.name}`}
                    className={`${styles.historyBranchButton} ${activeHistoryBranch?.branch_id === branchHistory.branch_id ? styles.historyBranchButtonActive : ''}`}
                    onClick={() => setActiveHistoryBranchId(branchHistory.branch_id)}
                  >
                    <span>{branchHistory.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>История рассылок пуста</div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.historyOfNotifications}>
        {activeHistoryBranch?.name && (
          <div className={styles.historyHeader}>
            <div>
              <div className={styles.historyTitle}>{activeHistoryBranch.name}</div>
              <div className={styles.historySubtitle}>История выбранного филиала отображается отдельно от выбора для рассылки</div>
            </div>
          </div>
        )}
        {activeHistoryBranch?.history && activeHistoryBranch.history.length > 0 ? (
          [...activeHistoryBranch.history].reverse().map((historyItem, index) => (
            <div key={index} className={styles.historyItem}>
              <div className={styles.historyTop}>
                <div>{format(new Date(historyItem.last_date), 'dd.MM.yyyy')}</div>
              </div>
              <div className={styles.historyCenter}>
                {historyItem.message}
              </div>
              <div className={styles.historyBottom}>
                <div>{historyItem.sent_count}</div>
                {historyItem.completed_date ? (
                  <div>{format(new Date(historyItem.completed_date), 'dd.MM.yyyy')}</div>
                ) : null}
              </div>
              <FailedNotificationLine item={historyItem} />
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            {historyOfNotifications.length > 0 ? 'История рассылок пуста' : 'Выберите филиал для просмотра истории'}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientsDatabaseSendNotification
